/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { FC, ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import colors from '../../themes/main-colors';
import { ExpandMore, CheckCircle } from '@material-ui/icons';
import { PageTextContentMap, ProductData } from '../../hooks/useContentMgmt';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Block, Inline } from '@contentful/rich-text-types';
import Buttons from '../buttons';
import { isMobile } from 'react-device-detect';
import IconChargerLevel2 from '../pge-plus-common/static/evChargerIcon.svg';

// this import is available in library's v15
// import { BLOCKS } from '@contentful/rich-text-types';
const BLOCKS = {
  DOCUMENT: 'document',
  PARAGRAPH: 'paragraph',
  HEADING_1: 'heading-1',
  HEADING_2: 'heading-2',
  HEADING_3: 'heading-3',
  HEADING_4: 'heading-4',
  HEADING_5: 'heading-5',
  HEADING_6: 'heading-6',
  OL_LIST: 'ordered-list',
  UL_LIST: 'unordered-list',
  LIST_ITEM: 'list-item',
  HR: 'hr',
  QUOTE: 'blockquote',
  EMBEDDED_ENTRY: 'embedded-entry-block',
  EMBEDDED_ASSET: 'embedded-asset-block',
  EMBEDDED_RESOURCE: 'embedded-resource-block',
  TABLE: 'table',
  TABLE_ROW: 'table-row',
  TABLE_CELL: 'table-cell',
  TABLE_HEADER_CELL: 'table-header-cell',
};

const options = {
  renderNode: {
    [BLOCKS.UL_LIST]: (node: Block | Inline, children: ReactNode) => (
      <ul css={{ paddingLeft: 24 }}>{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (node: Block | Inline, children: ReactNode) => (
      <ol css={{ paddingLeft: 24 }}>{children}</ol>
    ),
    [BLOCKS.TABLE]: (node: Block | Inline, children: ReactNode) => (
      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    ),
    [BLOCKS.TABLE_HEADER_CELL]: (node: Block | Inline, children: ReactNode) => (
      <TableCell align="center" css={{ fontWeight: 'bold' }}>
        {children}
      </TableCell>
    ),
    [BLOCKS.TABLE_ROW]: (node: Block | Inline, children: ReactNode) => (
      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
        {children}
      </TableRow>
    ),
    [BLOCKS.TABLE_CELL]: (node: Block | Inline, children: ReactNode) => (
      <TableCell align="center">{children}</TableCell>
    ),
  },
};

interface ProductCardExpandableSummaryProps {
  children?: ReactNode;
  product: ProductData;
  isActive: boolean;
  handleSelect: (event: React.MouseEvent<HTMLElement>) => void;
}
export const ProductCardExpandableSummary: FC<ProductCardExpandableSummaryProps &
  PageTextContentMap> = ({
  product,
  isActive,
  handleSelect,
  pageContentMap,
}) => (
  <Box display="flex" flexDirection="row" width="100%" minHeight="200px">
    {!product.outOfStock && (
      <Box width="35%" minWidth="145px" position="relative">
        {product.images ? (
          <img
            src={product?.images?.[0]?.fields?.file?.url}
            alt={product?.entryName}
            css={css`
              width: 100%;
              height: 100%;
              object-fit: cover;
            `}
          />
        ) : (
          <Skeleton
            variant="rect"
            css={css`
              width: 100%;
              height: 100%;
            `}
          />
        )}
        {isActive && (
          <CheckCircle
            css={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '18px',
              height: '18px',
            }}
            htmlColor={colors.shoreGreen}
          />
        )}
      </Box>
    )}
    {product.outOfStock && (
      <Box
        width="35%"
        minWidth="145px"
        position="relative"
        display="flex"
        justifyContent="center"
        alignItems="center"
        css={{ background: colors.lightGray2 }}
      >
        <IconChargerLevel2 height={170} width={145} />
      </Box>
    )}
    <Box
      width={'65%'}
      padding="20px"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      css={{
        ...(isActive ? { background: colors.navBarHoverBackground } : {}),
      }}
    >
      <Box css={{ paddingRight: '12px' }}>
        <Typography
          css={{
            fontSize: isMobile ? '16px' : '18px',
            marginBottom: '4px',
            lineHeight: '26px',
          }}
        >
          {product.entryName}
        </Typography>

        <Typography
          css={{ fontSize: isMobile ? '14px' : '16px', marginBottom: '4px' }}
        >
          {pageContentMap.get('SOLD_BY')} {product?.manufacturer}
        </Typography>

        <Typography
          css={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold' }}
        >
          ${product.price}
        </Typography>
      </Box>

      <Box display={'flex'} justifyContent={'end'}>
        {!product.outOfStock && (
          <Buttons
            variant="contained"
            onClick={e => {
              e.stopPropagation();
              handleSelect(e);
            }}
            color="secondary"
            css={{
              padding: '10px 65px',
              fontSize: 18,
              marginTop: 50,
              borderRadius: 30,
              backgroundColor: colors.sparkBlue,
              color: colors.white,
              '&:hover': {
                backgroundColor: colors.sparkBlue,
                borderColor: colors.sparkBlue,
              },
            }}
          >
            {pageContentMap.get('SELECT')}
          </Buttons>
        )}
        {product.outOfStock && (
          <Typography css={{ fontSize: '20px' }}>Out of Stock</Typography>
        )}
      </Box>
    </Box>
  </Box>
);

interface ProductCardExpandableDetailsProps {
  children?: React.ReactNode;
  product: ProductData;
}
const ProductCardExpandableDetails: React.FC<ProductCardExpandableDetailsProps &
  PageTextContentMap> = ({ product, pageContentMap }) => {
  return (
    <Box fontSize="14px">
      <Typography css={{ fontWeight: 'bold', fontSize: '16px' }}>
        {pageContentMap.get('PRODUCT_DESCRIPTION')}
      </Typography>
      <Box>{documentToReactComponents(product.description!, options)}</Box>
      <Box>{documentToReactComponents(product.details!, options)}</Box>
      <Box>{documentToReactComponents(product.efficacy!, options)}</Box>
    </Box>
  );
};

interface ProductCardExpandableProps {
  children?: React.ReactNode;
  product: ProductData;
  isActive: boolean;
  handleSelect: (event: React.MouseEvent<HTMLElement>) => void;
}

const ProductCardExpandable: React.FC<ProductCardExpandableProps &
  PageTextContentMap> = ({
  product,
  isActive,
  handleSelect,
  pageContentMap,
}) => {
  return (
    <Box>
      <Accordion
        sx={{ boxShadow: 1 }}
        css={css`
          & .MuiAccordionSummary-expandIcon {
            position: absolute;
            right: 3px;
            top: 10px;
            margin: 0px;
          }
          &.MuiIconButton-root {
            padding: 0px;
          }
        `}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          // disableGutters={true}

          css={{
            padding: '0px',
            '& .MuiAccordionSummary-content': {
              margin: '0px',
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: '0px',
            },
            ...(isActive ? { border: '1px solid ' + colors.sparkBlue } : {}),
          }}
        >
          <ProductCardExpandableSummary
            product={product}
            isActive={isActive}
            handleSelect={handleSelect}
            pageContentMap={pageContentMap}
          />
        </AccordionSummary>
        <AccordionDetails>
          <ProductCardExpandableDetails
            product={product}
            pageContentMap={pageContentMap}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ProductCardExpandable;
